import { createContext, useCallback, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { setAuthBridge } from '../lib/api'

interface AuthUser {
  id: string
  email: string
  displayName: string
  emailVerified: boolean
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  isLoading: boolean
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName: string) => Promise<void>
  logout: () => Promise<void>
  /** 有効なアクセストークンを返す。失効（間近）なら先にリフレッシュする */
  getAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000'

// アクセストークンの有効期限は15分。時計のずれや通信時間で「送った瞬間に失効」
// となるのを避けるため、残りがこの秒数を切ったら先にリフレッシュする。
const EXPIRY_MARGIN_SEC = 30

/** JWT の exp（秒）を取り出す。取れない場合は null（=期限判定せず 401 で対処） */
function tokenExpiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: number }).exp
    return typeof exp === 'number' ? exp : null
  } catch {
    return null
  }
}

function isUsable(token: string): boolean {
  const exp = tokenExpiresAt(token)
  if (exp === null) return true
  return exp - EXPIRY_MARGIN_SEC > Date.now() / 1000
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, accessToken: null, isLoading: true })

  // state は非同期処理の中から最新値を読めないため、トークンは ref にも保持する。
  const tokenRef = useRef<string | null>(null)
  // リフレッシュトークンはサーバ側でローテーションされるため、同時に複数回
  // /auth/refresh を叩くと後続が「無効なトークン」になりセッションが壊れる。
  // 進行中のリフレッシュは 1 本に集約する。
  const refreshingRef = useRef<Promise<string | null> | null>(null)

  const applyToken = useCallback((token: string | null, user?: AuthUser) => {
    tokenRef.current = token
    setState((prev) => ({
      user: user ?? (token ? prev.user : null),
      accessToken: token,
      isLoading: false,
    }))
  }, [])

  /**
   * リフレッシュトークン（httpOnly Cookie）でアクセストークンを再発行する。
   * - 401 等でリフレッシュ自体が拒否された場合はセッションを破棄する
   *   （ProtectedRoute がログイン画面へリダイレクトする）
   * - 通信エラーはオフラインの可能性があるためセッションを維持する
   */
  const refreshAccessToken = useCallback(async (staleToken?: string | null): Promise<string | null> => {
    // 別のリクエストが既に更新済みなら、それを使い回す（無駄なローテーション回避）
    if (staleToken && tokenRef.current && tokenRef.current !== staleToken) {
      return tokenRef.current
    }
    if (refreshingRef.current) return refreshingRef.current

    const pending = (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, { method: 'POST', credentials: 'include' })
        if (!res.ok) {
          applyToken(null)
          return null
        }
        const body = await res.json() as { data: { accessToken: string } }
        applyToken(body.data.accessToken)
        return body.data.accessToken
      } catch {
        setState((prev) => ({ ...prev, isLoading: false }))
        return null
      } finally {
        refreshingRef.current = null
      }
    })()

    refreshingRef.current = pending
    return pending
  }, [applyToken])

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const token = tokenRef.current
    if (token && isUsable(token)) return token
    return refreshAccessToken(token)
  }, [refreshAccessToken])

  // API 層（lib/api.ts）からトークンの取得・更新を呼べるようにしておく。
  // 各ページが渡すトークンはレンダリング時点のスナップショットなので、
  // 送信直前の最新値はここ経由で解決される。
  useEffect(() => {
    setAuthBridge({ getAccessToken, refreshAccessToken })
    return () => setAuthBridge(null)
  }, [getAccessToken, refreshAccessToken])

  // StrictMode は開発モードで useEffect を 2 回実行する。バックエンドの
  // /auth/refresh はリフレッシュトークンをローテーションするため、素直に 2 回
  // 呼ぶと 1 回目で無効化されたトークンを 2 回目が送ってしまい 401 になり、
  // 復元できたはずのセッションが破棄される。初回の 1 回だけ実行する。
  const bootstrapped = useRef(false)

  useEffect(() => {
    if (bootstrapped.current) return
    bootstrapped.current = true
    void refreshAccessToken()
  }, [refreshAccessToken])

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })
    const body = await res.json() as { data?: { user: AuthUser; accessToken: string }; error?: { message: string } }
    if (!res.ok) throw new Error(body.error?.message ?? 'ログインに失敗しました')
    applyToken(body.data!.accessToken, body.data!.user)
  }

  const register = async (email: string, password: string, displayName: string) => {
    const res = await fetch(`${API_BASE}/api/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, displayName }),
    })
    const body = await res.json() as { data?: { user: AuthUser; accessToken: string }; error?: { message: string } }
    if (!res.ok) throw new Error(body.error?.message ?? '登録に失敗しました')
    applyToken(body.data!.accessToken, body.data!.user)
  }

  const logout = async () => {
    await fetch(`${API_BASE}/api/v1/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tokenRef.current ?? ''}` },
      credentials: 'include',
    }).catch(() => undefined)
    tokenRef.current = null
    setState({ user: null, accessToken: null, isLoading: false })
  }

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
